import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample news data (will be replaced with API data when available)
const sampleNews = [
  {
    id: '1',
    title: 'New WASH Training Program Launches in Buea',
    excerpt: 'SMART Centre Cameroon announces a comprehensive water sanitation and hygiene training program starting next month at our Buea satellite centre.',
    content: 'Full article content here...',
    image_url: 'https://images.unsplash.com/photo-1651025674254-bd69d5d550f0?w=600',
    category: 'Training',
    created_at: '2026-01-15T10:00:00Z'
  },
  {
    id: '2',
    title: 'Community Borehole Project Completed in Limbe',
    excerpt: 'We are proud to announce the successful completion of our hand-drilled borehole project serving over 300 families in the Limbe area.',
    content: 'Full article content here...',
    image_url: 'https://images.unsplash.com/photo-1740741705746-74e6f9b93b4f?w=600',
    category: 'Impact',
    created_at: '2026-01-10T14:00:00Z'
  },
  {
    id: '3',
    title: 'Partnership with SMART Centre Group Expanded',
    excerpt: 'Our partnership with SMART Centre Group (Holland) has been expanded to include new technology transfers and international training opportunities.',
    content: 'Full article content here...',
    image_url: 'https://images.unsplash.com/photo-1627423893729-3a79f48ff473?w=600',
    category: 'News',
    created_at: '2026-01-05T09:00:00Z'
  },
  {
    id: '4',
    title: 'Mushroom Farming Success Stories',
    excerpt: 'Meet the graduates of our mushroom farming program who are now running successful businesses and supporting their families.',
    content: 'Full article content here...',
    image_url: 'https://images.unsplash.com/photo-1737652384220-078e824d5af7?w=600',
    category: 'Success Story',
    created_at: '2025-12-28T11:00:00Z'
  },
  {
    id: '5',
    title: 'Upcoming Food Science Workshop',
    excerpt: 'Register now for our upcoming food science workshop covering preservation, packaging, and processing techniques.',
    content: 'Full article content here...',
    image_url: 'https://images.unsplash.com/photo-1744809482817-9a9d4fc280af?w=600',
    category: 'Training',
    created_at: '2025-12-20T16:00:00Z'
  },
  {
    id: '6',
    title: 'Solar Pump Installation Training Complete',
    excerpt: 'Another successful cohort has completed our solar pump installation training, ready to serve communities across the region.',
    content: 'Full article content here...',
    image_url: 'https://images.unsplash.com/photo-1584789900013-7eb27b6dae05?w=600',
    category: 'Training',
    created_at: '2025-12-15T10:00:00Z'
  }
];

const categories = ['All', 'Training', 'Impact', 'News', 'Success Story'];

export default function NewsPage() {
  const [news, setNews] = useState(sampleNews);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/news`);
        if (response.data && response.data.length > 0) {
          setNews(response.data);
        }
      } catch (error) {
        console.log('Using sample news data');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const filteredNews = selectedCategory === 'All' 
    ? news 
    : news.filter(item => item.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="news-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-primary" data-testid="news-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">Updates</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 font-heading">
              News & Blog
            </h1>
            <p className="text-xl text-blue-100">
              Stay updated with our latest training announcements, impact stories, and community news.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-accent" data-testid="news-filter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium text-sm transition-all ${
                  selectedCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 md:py-24 bg-white" data-testid="news-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-slate-600 mt-4">Loading news...</p>
            </div>
          ) : filteredNews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No news articles found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((article, index) => (
                <motion.article
                  key={article.id}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`news-article-${index}`}
                >
                  <div className="aspect-video overflow-hidden img-zoom">
                    <img 
                      src={article.image_url} 
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-secondary bg-secondary/10 px-3 py-1 rounded-full">
                        <Tag className="h-3 w-3" />
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar className="h-3 w-3" />
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <button className="inline-flex items-center gap-1 text-primary font-medium text-sm hover:text-secondary transition-colors">
                      Read More <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
            Want to Stay Updated?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Follow us on social media or contact us to receive updates about upcoming trainings and events.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-secondary hover:bg-green-50 rounded-full px-8 py-4 font-semibold text-lg transition-all shadow-lg"
            data-testid="news-contact-btn"
          >
            Contact Us <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
